import React, { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiTrash2 } from 'react-icons/fi';
import {
  createProductVarietyGroup,
  deleteProductVarietyGroup,
  reorderProductVarietyGroups,
  replaceProductVarietyGroupVarieties,
  updateProductVarietyGroup,
} from '../api';
import HorizontalPillTabs from './HorizontalPillTabs';
import InlineLoading from './InlineLoading';
import { normalizeVarietyGroups } from '../utils/varietyGroups';

const toId = (value) => (value === null || value === undefined ? '' : String(value));

const VarietyGroupManager = ({
  branchId,
  product,
  canUpdateProduct = true,
}) => {
  const groups = useMemo(
    () => normalizeVarietyGroups(product?.VarietyGroups),
    [product?.VarietyGroups]
  );
  const varieties = Array.isArray(product?.Varieties) ? product.Varieties : [];
  const [activeGroupId, setActiveGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [draftVarietyIds, setDraftVarietyIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedGroup = groups.find((group) => toId(group.id) === toId(activeGroupId)) || null;
  const selectedIdSet = useMemo(
    () => new Set(draftVarietyIds.map((id) => Number(id))),
    [draftVarietyIds]
  );
  const isDirty = selectedGroup
    ? JSON.stringify([...selectedIdSet].sort((a, b) => a - b)) !== JSON.stringify(
        [...new Set((selectedGroup.varietyIds || []).map(Number))].sort((a, b) => a - b)
      )
    : false;

  useEffect(() => {
    if (!groups.length) {
      setActiveGroupId('');
      return;
    }
    if (groups.some((group) => toId(group.id) === toId(activeGroupId))) return;
    setActiveGroupId(toId(groups[0].id));
  }, [groups, activeGroupId]);

  useEffect(() => {
    setRenameValue(selectedGroup?.name || '');
    setDraftVarietyIds(selectedGroup?.varietyIds || []);
    setError('');
  }, [selectedGroup?.id, selectedGroup?.name, selectedGroup?.varietyIds]);

  if (!product?.id) return null;

  const run = async (callback) => {
    if (!canUpdateProduct || !branchId || !product?.id) return;
    setSaving(true);
    setError('');
    try {
      await callback();
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'No se pudo guardar el grupo.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = () => run(async () => {
    const name = newGroupName.trim();
    if (!name) {
      setError('El nombre del grupo es obligatorio.');
      return;
    }
    const response = await createProductVarietyGroup(branchId, product.id, { name });
    setNewGroupName('');
    const createdId = response?.group?.id;
    if (createdId) setActiveGroupId(toId(createdId));
  });

  const handleRename = () => run(async () => {
    if (!selectedGroup) return;
    const name = renameValue.trim();
    if (!name) {
      setError('El nombre del grupo es obligatorio.');
      return;
    }
    await updateProductVarietyGroup(branchId, product.id, selectedGroup.id, { name });
  });

  const handleDelete = () => run(async () => {
    if (!selectedGroup) return;
    await deleteProductVarietyGroup(branchId, product.id, selectedGroup.id);
  });

  const handleMove = (direction) => run(async () => {
    if (!selectedGroup) return;
    const currentIndex = groups.findIndex((group) => toId(group.id) === toId(selectedGroup.id));
    const nextIndex = currentIndex + direction;
    if (currentIndex < 0 || nextIndex < 0 || nextIndex >= groups.length) return;
    const nextGroups = groups.slice();
    const [moved] = nextGroups.splice(currentIndex, 1);
    nextGroups.splice(nextIndex, 0, moved);
    await reorderProductVarietyGroups(branchId, product.id, nextGroups.map((group) => group.id));
  });

  const handleToggleVariety = (varietyId) => {
    setDraftVarietyIds((prev) => {
      const next = new Set(prev.map(Number));
      const id = Number(varietyId);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return Array.from(next);
    });
  };

  const handleSaveVarieties = () => run(async () => {
    if (!selectedGroup) return;
    await replaceProductVarietyGroupVarieties(branchId, product.id, selectedGroup.id, draftVarietyIds);
  });

  return (
    <section className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Grupos de variedades</h3>
          <p className="text-xs text-gray-500">Estos grupos filtran las variedades dentro de la tarjeta del producto.</p>
        </div>
        {saving && <InlineLoading label="Guardando grupos" />}
      </div>

      {canUpdateProduct && (
        <div className="mb-3 flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={newGroupName}
            onChange={(event) => setNewGroupName(event.target.value)}
            placeholder="Nuevo grupo"
            className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
            disabled={saving}
          />
          <button
            type="button"
            onClick={handleCreate}
            disabled={saving}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            Agregar grupo
          </button>
        </div>
      )}

      {groups.length > 0 ? (
        <>
          <HorizontalPillTabs
            items={groups.map((group) => ({ id: toId(group.id), label: group.name }))}
            activeId={activeGroupId}
            onSelect={(id) => setActiveGroupId(toId(id))}
            className="mb-3"
          />

          {selectedGroup && (
            <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-3">
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <input
                  type="text"
                  value={renameValue}
                  onChange={(event) => setRenameValue(event.target.value)}
                  className="min-w-0 flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm"
                  disabled={!canUpdateProduct || saving}
                />
                {canUpdateProduct && (
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleRename}
                      disabled={saving || renameValue.trim() === selectedGroup.name}
                      className="rounded border border-blue-300 px-2 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-60"
                    >
                      Renombrar
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(-1)}
                      disabled={saving || groups[0]?.id === selectedGroup.id}
                      className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      title="Subir"
                      aria-label="Subir grupo"
                    >
                      <FiChevronUp size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMove(1)}
                      disabled={saving || groups[groups.length - 1]?.id === selectedGroup.id}
                      className="rounded border border-gray-300 p-1.5 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      title="Bajar"
                      aria-label="Bajar grupo"
                    >
                      <FiChevronDown size={15} />
                    </button>
                    <button
                      type="button"
                      onClick={handleDelete}
                      disabled={saving}
                      className="rounded border border-red-300 p-1.5 text-red-700 hover:bg-red-50 disabled:opacity-60"
                      title="Eliminar"
                      aria-label="Eliminar grupo"
                    >
                      <FiTrash2 size={15} />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid max-h-48 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                {varieties.map((variety) => (
                  <label key={variety.id} className="flex items-center gap-2 rounded border border-gray-100 px-2 py-1.5 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={selectedIdSet.has(Number(variety.id))}
                      onChange={() => handleToggleVariety(variety.id)}
                      disabled={!canUpdateProduct || saving}
                    />
                    <span className="min-w-0 truncate">{variety.varietyName}</span>
                  </label>
                ))}
                {varieties.length === 0 && (
                  <p className="text-xs text-gray-500">Este producto no tiene variedades.</p>
                )}
              </div>

              {canUpdateProduct && (
                <button
                  type="button"
                  onClick={handleSaveVarieties}
                  disabled={saving || !isDirty}
                  className="rounded bg-green-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
                >
                  Guardar variedades del grupo
                </button>
              )}
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-gray-500">No hay grupos configurados para este producto.</p>
      )}

      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </section>
  );
};

export default VarietyGroupManager;
